import { termFor, TONE_STYLES } from '../../../util/term'

/**
 * Fixed 6-cell score grid + 1 overflow ("...") cell — visual rhythm is
 * the same regardless of the player's strokes given on the hole. Per
 * UX consult round 1: variable button count between players was jarring
 * for the captain entering scores for the whole flight.
 *
 * Cells render gross par-2 through par+3. The 7th "..." cell opens a
 * number-pad sheet for the rare case where a high-hcp player tanks a
 * hole beyond par+3 (still scorable — net double-bogey for them).
 *
 * Selected gross > par+3 still highlights the overflow cell (shows the
 * actual number) so the card communicates state correctly.
 */
const ScoreButtons = ({
  par,
  selected,
  disabled,
  onPick,
  onOverflow,
}: {
  par: number
  selected: number | null | undefined
  disabled: boolean
  onPick: (gross: number) => void
  onOverflow: () => void
}) => {
  const start = Math.max(1, par - 2)
  const STANDARD = Array.from({ length: 6 }, (_, i) => start + i)
  const overflowActive = selected != null && selected > start + 5
  return (
    <div
      className="grid gap-1.5"
      style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
    >
      {STANDARD.map((g) => {
        const t = termFor(g, par)
        const isSel = g === selected
        const tone = TONE_STYLES[t.tone]
        return (
          <button
            key={g}
            type="button"
            disabled={disabled}
            onClick={() => onPick(g)}
            className={`flex flex-col items-center justify-center py-2 rounded font-medium transition-colors disabled:opacity-50 ${
              isSel
                ? `${tone.bg} ${tone.fg} ring-2 ${tone.ring}`
                : 'bg-cream-card text-ink-700'
            }`}
          >
            <span className="font-mono num text-base font-bold leading-none">
              {g}
            </span>
            <span className="text-[10px] leading-tight mt-1">{t.label}</span>
          </button>
        )
      })}
      {/* Overflow cell — opens the sheet to enter a high gross. If a
          high-gross score is already set, tapping clears (consistent
          with the rest of the grid). To edit an existing overflow,
          clear first, then tap again to re-open. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => (overflowActive ? onPick(selected!) : onOverflow())}
        aria-label={overflowActive ? 'Fjern score' : 'Høyere score'}
        className={`flex flex-col items-center justify-center py-2 rounded font-medium transition-colors disabled:opacity-50 ${
          overflowActive
            ? 'bg-ink-900 text-paper ring-2 ring-black'
            : 'bg-cream-card text-ink-700'
        }`}
      >
        <span className="font-mono num text-base font-bold leading-none">
          {overflowActive ? selected : '…'}
        </span>
        <span className="text-[10px] leading-tight mt-1">
          {overflowActive ? `+${selected! - par}` : 'Mer'}
        </span>
      </button>
    </div>
  )
}

export default ScoreButtons
